import { takeLatest, put, all, call, takeEvery } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as actions from "./actions";
import { db } from "../../helpers/firebase/firebaseConfig";
import { doc, collection, getDocs,getDoc, addDoc, updateDoc, deleteDoc, query, where, writeBatch } from "firebase/firestore";


// Collection names
const SERVICES_COLLECTION = "service_provider_services";
const CATEGORIES_COLLECTION = "categories";
const TYPES_COLLECTION = "service_type";

// Node API base
const NODE_API = "https://node-server-eta-coral.vercel.app";
const NODE_API_KEY = process.env.REACT_APP_NODE_API_KEY;

// File -> base64 (no data: prefix)
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || "";
    const base64 = String(result).replace(/^data:[^;]+;base64,/, '');
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Helper: create ERP Item via proxy
async function createERPItem(itemPayload) {
  const res = await fetch(`${NODE_API}/item`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "x-api-key": NODE_API_KEY,
    },
    body: JSON.stringify(itemPayload),
  });
  const json = await res.json();
  if (!res.ok || json?.error || json?.exc_type) {
    const message =
      json?.message || json?.error || json?._server_messages || "ERP Item creation failed";
    throw new Error(typeof message === "string" ? message : JSON.stringify(message));
  }
  return json?.data || json;
}

// Worker Saga for adding a new service
async function addService(newService) {
  try {
    // Validate required fields early
    if (!newService.provider_id) {
      throw new Error("Missing provider_id: Cannot create service without a valid provider.");
    }
    if (!newService.img_file) {
      throw new Error("Missing image file.");
    }

    const img = newService.img_file;

    // Upload image via Node server (avoid browser->Spaces CORS)
    const fileBase64 = await fileToBase64(img);
    const uploadRes = await fetch(`${NODE_API}/upload-service-image`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": NODE_API_KEY,
      },
      body: JSON.stringify({
        fileName: img.name,
        contentType: img.type || "application/octet-stream",
        fileBase64,
        folder: "services",
      }),
    });
    if (!uploadRes.ok) {
      const err = await uploadRes.text();
      throw new Error(err || "Image upload failed");
    }
    const { url: imgUrl, key: imgKey } = await uploadRes.json();

    const serviceData = {
      service_provider_id: newService.provider_id,
      service_provider_auth_id: newService.provider_id,
      name: newService.name,
      service_price: Number(newService.service_price),
      img_url: imgUrl || null,
      img_id: imgKey,
      description: newService.description,
      service_type: newService.service_type,
      service_category: newService.category_type,
      avg_execute_time: Number(newService.avg_execute_time),
      at_home_service_cost: Number(newService.at_home_service_price),
      serviceProviders: `${newService.provider_id}`,
      createdAt: new Date().toISOString(),
      is_deleted: false,
      status: "active",
      // erp_item_code will be added after successful ERP creation
    };

    // 1) Create service in Firestore
    const docRef = await addDoc(collection(db, SERVICES_COLLECTION), serviceData);

    // 2) Create ERP Item (best-effort, non-blocking for service creation)
    try {
      const codeSuffix = docRef.id.slice(0, 6).toUpperCase();
      const item_code = `DB-SRV-${codeSuffix}`;
      const erpPayload = {
        item_code,
        item_name: newService.name,
        item_group: "Services",
        stock_uom: "Nos",
        is_stock_item: 0,            // added to match endpoint
        is_sales_item: 1,            // added to match endpoint
        description: newService.description || "",
        item_tax_template: "Standard Vat 15% - DS" // keep tax template
      };

      await createERPItem(erpPayload);

      // Persist ERP item info on the service
      await updateDoc(doc(db, SERVICES_COLLECTION, docRef.id), {
        erp_item_code: erpPayload.item_code,
        erp_item_group: erpPayload.item_group,
        stock_uom: erpPayload.stock_uom,
        item_tax_template: erpPayload.item_tax_template,
        erp_item_created_at: new Date().toISOString(),
      });

      return {
        id: docRef.id,
        ...serviceData,
        erp_item_code: erpPayload.item_code,
        erp_item_group: erpPayload.item_group,
        stock_uom: erpPayload.stock_uom,
        item_tax_template: erpPayload.item_tax_template,
      };
    } catch (erpErr) {
      // Log and return service without ERP linkage; UI can inform admin if needed
      console.error("ERP Item creation failed:", erpErr.message);
      return { id: docRef.id, ...serviceData };
    }
  } catch (error) {
    console.error("Error adding service:", error);
    throw error;
  }
}

function* addServiceSaga(action) {
  try {
    const response = yield call(addService, action.payload);
    yield put(actions.addServiceSuccess(response));
  } catch (error) {
    console.error("addServiceSaga error:", error.message);
    // yield put(actions.addServiceFailure(error.message));
  }
}

// Worker Saga for updating a service
// Worker Saga for updating a service
async function updateServiceData(id, updatedService) {

  const updatedFormData = {
    name: updatedService.name,
    service_price: Number(updatedService.service_price),
    description: updatedService.description,
    service_type: updatedService.service_type,
    service_category: updatedService.service_category,
    avg_execute_time: Number(updatedService.avg_execute_time || 0),
    at_home_service_cost: Number(updatedService.at_home_service_cost || 0),
  };

  try {
    await updateDoc(doc(db, SERVICES_COLLECTION, id), updatedFormData);
    return { id, ...updatedFormData };
  } catch (error) {
    console.error("Error updating service:", error);
    throw error;
  }
}

function* updateServiceSaga(action) {
  try {
    const updatedService = action.payload;
    const response = yield call(updateServiceData, updatedService.id, updatedService);

    yield put(actions.fetchServicesRequest()); // Refresh the list
  } catch (error) {
    console.error("Error updating service:", error);
    // yield put(actions.updateServiceFailure(error));
  }
}

async function removeService(id) {
  const deletionBatch = writeBatch(db);

  try {
    // 1. Get service document
    const serviceRef = doc(db, "service_provider_services", id);
    const serviceDoc = await getDoc(serviceRef);
    
    if (!serviceDoc.exists()) {
      throw new Error("Service not found");
    }
    const serviceData = serviceDoc.data();

    // 2. Create audit log of deletion (for reference)
    const deletionData = {
      service_id: id,
      service_name: serviceData.name,
      service_data: serviceData,
      deleted_at: new Date().toISOString(),
      deleted_by: "Admin", // Or get current user ID if available
      deletion_type: "soft_delete",
      note: "Service marked as deleted. All related bookings, invoices, and references are preserved for historical records."
    };

    // 3. Create audit log document
    const logRef = doc(collection(db, "services_deletion_logs"));
    deletionBatch.set(logRef, deletionData);

    // 4. Mark service as deleted (SOFT DELETE - not hard delete)
    deletionBatch.update(serviceRef, {
      is_deleted: true,
      status: "archived",
      deleted_at: new Date().toISOString(),
      deleted_by: "Admin",
      updated_at: new Date().toISOString()
    });

    // 5. Commit the soft delete operation
    await deletionBatch.commit();

    // 6. Optional: Delete service image from storage (via Node server) - keeping image deletion optional
    if (serviceData.img_id) {
      try {
        const delRes = await fetch(`${NODE_API}/delete-service-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": NODE_API_KEY,
          },
          body: JSON.stringify({ key: serviceData.img_id }),
        });
        if (!delRes.ok) {
          const err = await delRes.text();
          console.warn("Image deletion error - service remains accessible:", err);
        }
      } catch (storageError) {
        console.warn("Image deletion error - service remains accessible:", storageError);
      }
    }

    // 7. Return success - service marked as deleted but data preserved
    return id;

  } catch (error) {
    console.error("Soft delete failed:", error);
    throw new Error(`Failed to archive service: ${error.message}`);
  }
}
function* removeServiceSaga(action) {
  try {
    const serviceId = action.payload;

    yield call(removeService, serviceId);
    yield put(actions.removeServiceSuccess(serviceId));
    // toast.success("Service and all related data were deleted successfully");
  } catch (error) {
    console.error("Error in removeServiceSaga:", error);
    // yield put(actions.removeServiceFailure(error.message));
    toast.error(error.message);
  }
}

// Worker Saga for fetching services
// By default excludes deleted/archived services
async function fetchServices(includeDeleted = false) {
  try {
    // Fetch all services from Firestore
    const querySnapshot = await getDocs(collection(db, SERVICES_COLLECTION));
    
    // Map to array with id
    const allServices = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter in JavaScript to handle documents without is_deleted field
    if (includeDeleted) {
      return allServices;
    } else {
      // Exclude services where is_deleted === true
      // Services without is_deleted field are considered active
      return allServices.filter(service => service.is_deleted !== true);
    }
  } catch (error) {
    throw error;
  }
}

function* fetchServicesSaga() {
  try {
    const services = yield call(fetchServices);
    yield put(actions.fetchServicesSuccess(services));
  } catch (error) {
    yield put(actions.fetchServicesFailure(error.message));
    throw error;
  }
}

// Worker Saga for fetching categories
async function fetchCategories() {
  try {
    const querySnapshot = await getDocs(collection(db, CATEGORIES_COLLECTION));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
}

function* fetchCategoriesSaga() {
  try {
    const categories = yield call(fetchCategories);
    yield put(actions.fetchCategoriesSuccess(categories));
  } catch (error) {
    throw error;
  }
}


// Worker Saga for fetching types
async function fetchTypes() {
  try {
    const querySnapshot = await getDocs(collection(db, "service_type"));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching service types:", error);
    throw error;
  }
}

function* fetchTypesSaga() {
  try {
    const types = yield call(fetchTypes);

    yield put(actions.fetchTypesSuccess(types));
  } catch (error) {
    // yield put(actions.fetchTypesFailure(error.message));
  }
}



// Worker Saga for adding a category
async function addCategory(data) {
  try {
    const { title, img_file } = data;
    const categoryData = { title };

    if (img_file) {
      // Upload via Node server
      const fileBase64 = await fileToBase64(img_file);
      const uploadRes = await fetch(`${NODE_API}/upload-service-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": NODE_API_KEY,
        },
        body: JSON.stringify({
          fileName: img_file.name,
          contentType: img_file.type || "application/octet-stream",
          fileBase64,
          folder: "categories",
        }),
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.text();
        throw new Error(err || "Category image upload failed");
      }
      const { url: imgUrl, key: imgKey } = await uploadRes.json();
      categoryData.img_id = imgKey || null;
      categoryData.img_url = imgUrl || null;
    }

    const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), categoryData);
    return { id: docRef.id, ...categoryData };
  } catch (error) {
    throw error;
  }
}

function* addCategorySaga(action) {
  try {
    yield call(addCategory, action.payload);
    yield put(actions.fetchCategoriesRequest());
  } catch (error) {
    yield put(actions.fetchCategoriesRequest());
  }
}

// Worker Saga for deleting a category
async function deleteCategory(category) {
  try {
    const { img_id, id } = category;

    // Delete image via Node server
    if (img_id) {
      try {
        const delRes = await fetch(`${NODE_API}/delete-service-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": NODE_API_KEY,
          },
          body: JSON.stringify({ key: img_id }),
        });
        if (!delRes.ok) {
          const err = await delRes.text();
          console.error("Category image deletion error:", err);
        }
      } catch (storageError) {
        console.error("Category image deletion error:", storageError);
      }
    }

    // Delete the category document
    await deleteDoc(doc(db, CATEGORIES_COLLECTION, id));
  } catch (error) {
    throw error;
  }
}

function* removeCategorySaga(action) {
  try {
    yield call(deleteCategory, action.payload);
    yield put(actions.fetchCategoriesRequest());
  } catch (error) {
    throw error;
  }
}

// Watcher Saga
function* watchServiceSaga() {
  yield all([
    takeLatest(actionTypes.ADD_SERVICE, addServiceSaga),
    takeLatest(actionTypes.UPDATE_SERVICE, updateServiceSaga),
    takeLatest(actionTypes.REMOVE_SERVICE, removeServiceSaga),
    takeEvery(actionTypes.FETCH_SERVICES_REQUEST, fetchServicesSaga),
    takeLatest(actionTypes.FETCH_CATEGORIES_REQUEST, fetchCategoriesSaga),
    takeLatest(actionTypes.FETCH_TYPES_REQUEST, fetchTypesSaga),
    takeLatest(actionTypes.REMOVE_CATEGORY, removeCategorySaga),
    takeLatest(actionTypes.ADD_CATEGORY, addCategorySaga),
  ]);
}

export default watchServiceSaga;